// CP Sourcing member screen: dormant CPs in tiers, click-to-call, remark log.
// Visual language mirrors the Leads tab: shared SearchBar, LeadStatusChip and
// Button, plain Leads cards.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Typography, CircularProgress, Avatar, IconButton,
  Stack, Paper, Collapse, useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CallIcon from "@mui/icons-material/Call";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import action from "../../Redux Store/actions";
import { supabase } from "../../supabaseClient";
import { getDormantCps, getCpSiteVisits } from "../../Services/CpSourcingApis";
import { makeCall } from "../../Services/SmartFloApis";
import CpDetailDrawer from "./CpDetailDrawer";
import MyCallsView from "./MyCallsView";
import AddActivityModal from "./AddActivityModal";
import AddCpSiteVisit from "../../Modals/AddCpSiteVisit/AddCpSiteVisit";
import CpSiteVisitApprovalView from "./CpSiteVisitApprovalView";
import LeadsFilterPanel from "../../Subcomponents/LeadsFilterPanel";
import SearchBar from "../../Subcomponents/Search";
import LeadStatusChip from "../../Subcomponents/LeadStatusChip";
import SharedButton from "../../Subcomponents/Button";
import MobileFilterBar from "./Mobile/MobileFilterBar";
import MobileFilterSheet from "./Mobile/MobileFilterSheet";
import ActiveFilterChips from "./Mobile/ActiveFilterChips";
import { C, TIER_META } from "./cpSourcingTheme";
import {
  collectSourceOptions, narrowRows, countTiers, selectTier, fetchAllPages,
  growReveal,
} from "./cpSourcingList";

// Rows added each time the reader scrolls to the end of the rendered list.
// Purely a rendering budget — the whole list is already in memory, so growing
// it costs no network call.
const REVEAL_STEP = 20;

// How far before the end of the list to start revealing the next rows, so the
// spinner is usually replaced by content before the reader reaches it.
const REVEAL_MARGIN = "300px";

// The /dormant-cps `limit` cap (cpSourcingRoutes.js). The complete set is
// loaded as sequential pages of this size.
const FETCH_PAGE_SIZE = 100;

const CHIP_KEYS = ["all", "outreach", "escalate", "critical", "neverActive"];

// Nearest scrolling ancestor, or null for the viewport.
//
// This screen renders inside MainPage's own scroll container
// (MainPage.jsx: height 95% / overflowY auto), not the window. An
// IntersectionObserver left on the default root would still fire — ancestor
// clipping sees to that — but its rootMargin would expand the *viewport* rect
// while the container's clip is what actually binds, so the early-reveal margin
// would have no effect and the spinner would flash at every step. Observing
// against the real scroll parent is what makes rootMargin mean anything here.
// Resolved by walking the DOM rather than by importing MainPage's layout, so
// this keeps working if the page is ever nested differently.
function findScrollParent(node) {
  for (let el = node?.parentElement; el; el = el.parentElement) {
    const { overflowY } = window.getComputedStyle(el);
    if ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight) {
      return el;
    }
  }
  return null; // window/viewport
}

// self + every descendant userId (BFS over parentId), mirrors CallLogs hierarchy.
function getAllDescendantIds(all, rootId) {
  const seen = new Set(), q = [rootId];
  while (q.length) {
    const id = q.shift();
    if (seen.has(id)) continue;
    seen.add(id);
    all.filter((u) => u.parentId === id).forEach((u) => q.push(u.userId));
  }
  return [...seen];
}

// Leads' card treatment: 1px #EAECF0 hairline, 0.8rem radius, and Leads' own
// card elevation (Leadcard.jsx:2681-2689). The 4px tier-coloured left border and
// the hover lift are deliberately gone — Leads cards have neither.
const Surface = styled(Paper)({
  background: C.white, border: `1px solid ${C.border}`, borderRadius: "0.8rem",
  boxShadow: "0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)",
  position: "relative", zIndex: 1,
});

// Leads' card treatment: 1px #EAECF0 hairline, 0.8rem radius, and Leads' own
// card elevation (Leadcard.jsx:2681-2689). The 4px tier-coloured left border and
// the hover lift are deliberately gone — Leads cards have neither. Urgency now
// reads from the status chip and the tier subtab instead. The `sc` prop is
// intentionally still accepted so existing call sites need no change.
const CpCard = styled(Paper)(() => ({
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: "0.8rem",
  boxShadow: "0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)",
  overflow: "hidden", cursor: "pointer",
}));

// Leads' primary action button via the shared Button component.
// The shared Button never sets the HTML `disabled` attribute (it only dims and
// sets pointer-events:none), so `pointerEvents` blocks the mouse but not the
// keyboard. The explicit guard below is what actually prevents a second
// concurrent call via Tab+Enter while one is already in flight.
const CallBtn = ({ startIcon, disabled, onClick, children }) => (
  <SharedButton
    style={{
      color: disabled ? "#98A2B3" : "#ffffff",
      backgroundColor: disabled ? "#F2F4F7" : C.primary,
      border: disabled ? "1px solid #EAECF0" : `1px solid ${C.primary}`,
      fontSize: "1.4rem",
      lineHeight: "2rem",
      width: "auto",
    }}
    padding={"0.8rem 1.6rem"}
    btnName={children}
    btnIcon={startIcon}
    iconPosition={"left"}
    clicked={(e) => {
      if (disabled) return;
      onClick?.(e);
    }}
    disable={disabled}
  />
);

// Leads' chip treatment via the shared LeadStatusChip: Inter-Medium 500,
// 1.4rem/2rem, 1.6rem pill radius, no border. Colours come from TIER_META,
// which now holds Leads' chip palette.
const TierBadge = ({ tier }) => {
  const meta = TIER_META[tier] || TIER_META.neverActive;
  return (
    <LeadStatusChip
      style={{ color: meta.color, backgroundColor: meta.bg }}
      label={meta.label}
    />
  );
};

const fmtDate = (ms) =>
  ms ? new Date(ms).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "Never";

export default function CpSourcingMain() {
  const loggedInUser = useSelector((s) => s?.Auth?.user);
  const dispatch = useDispatch();
  const userId = loggedInUser?.userId || loggedInUser?.id;
  // Role config: "All" (see every assigned CP) vs "Team Wise" (self + descendants).
  const showMode = loggedInUser?.roleId?.accessibleTabs?.CpSourcing?.["Show CpSourcing"]?.show;
  // Dedicated visibility for the CP Site Visit approval list: "All" → every CP's
  // site visits; "Team Wise" (or unset) → the user's own bucket (self + descendants).
  const svShowMode = loggedInUser?.roleId?.accessibleTabs?.CpSourcing?.["Show CP Site Visit"]?.show;
  // Approve/reject gating (mirrors the Site Visit Approval tab): role must have the
  // CpSourcing "Approve" permission AND the user must be the CP's approver or above.
  const cpSvApprove = loggedInUser?.roleId?.accessibleTabs?.CpSourcing?.Approve?.isAccessible === true;
  // View-only access for the approval details modal (grantable to non-parent users).
  const cpSvViewDetails = loggedInUser?.roleId?.accessibleTabs?.CpSourcing?.["View Details"]?.isAccessible === true;
  // "All" scope → a granted user can act on every CP; else only their team (hierarchy).
  const cpSvAllMode = svShowMode === "All";

  // Resolved visibility: null while loading, then { scope:"all" } or { userIds:[...] }.
  const [visibility, setVisibility] = useState(null);
  const [svVisibility, setSvVisibility] = useState(null); // scope for the approval list
  const [teamIds, setTeamIds] = useState([]); // self + descendants — for approve eligibility

  // The complete dormant-CP set for this user's visibility, loaded once and
  // held in memory. Every narrowing control below (tier tab, CP Source, search)
  // works over this array, so counts and rows always agree with each other and
  // describe the whole dataset rather than a loaded page.
  const [allRows, setAllRows] = useState([]);
  const [activeTier, setActiveTier] = useState("all");
  const [search, setSearch] = useState("");
  // CP Source filter (multi-select, like every other Leads-panel filter).
  // pending = what the checkboxes show; applied = what the list is filtered by.
  const [pendingSources, setPendingSources] = useState([]);
  const [appliedSources, setAppliedSources] = useState([]);
  // How many of the matching rows are currently rendered. Reset to REVEAL_STEP
  // whenever the narrowing changes, so a fresh filter starts at the top.
  const [visibleCount, setVisibleCount] = useState(REVEAL_STEP);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCp, setSelectedCp] = useState(null);
  const [activityCp, setActivityCp] = useState(null); // CP for the Add Activity modal
  const [siteVisitCp, setSiteVisitCp] = useState(null); // CP for the CP Site Visit modal
  const [callingCpId, setCallingCpId] = useState(null);
  const [view, setView] = useState("cps"); // "cps" | "calls"
  // Per-card "View more" site-visit collapse: which card is open + lazy cache.
  const [expandedCpId, setExpandedCpId] = useState(null);
  const [svByCp, setSvByCp] = useState({});
  const [svLoadingCp, setSvLoadingCp] = useState(null);
  // Same breakpoint the Partners mobile view uses (ChannelPartner.jsx).
  const isMobile = useMediaQuery("(max-width:820px)");
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const toggleSiteVisits = async (cpId) => {
    if (expandedCpId === cpId) { setExpandedCpId(null); return; }
    setExpandedCpId(cpId);
    if (!svByCp[cpId]) {
      setSvLoadingCp(cpId);
      try {
        const res = await getCpSiteVisits(cpId, 5);
        if (res?.success) setSvByCp((m) => ({ ...m, [cpId]: res.data || [] }));
      } catch (e) {
        toast.error("Failed to load site visits", { position: "top-center", autoClose: 3000 });
      } finally {
        setSvLoadingCp(null);
      }
    }
  };

  useEffect(() => { dispatch(action.activeMenu("cpsourcing")); }, [dispatch]);

  // Resolve visibility from role config + org tree (same model as CallLogs):
  //   "All" mode            → scope:"all" (every assigned CP)
  //   root user (no parent) → scope:"all"
  //   otherwise             → userIds = self + all descendants
  useEffect(() => {
    if (!userId) return;
    (async () => {
      if (showMode === "All") { setVisibility({ scope: "all" }); return; }
      try {
        const { data, error } = await supabase.from("Users").select("userId, parentId");
        if (error) throw error;
        const me = data.find((u) => u.userId === userId);
        const hasParent = me?.parentId !== null && me?.parentId !== undefined;
        if (!hasParent) { setVisibility({ scope: "all" }); return; } // root sees all
        setVisibility({ userIds: getAllDescendantIds(data, userId) });
      } catch (e) {
        console.error("CpSourcing hierarchy resolve error:", e);
        setVisibility({ userIds: [userId] }); // safe fallback: own only
      }
    })();
  }, [userId, showMode]);

  // Approval-list visibility (Show CP Site Visit): "All" → every assigned CP's
  // site visits; else Team Wise (self + descendants — the user's own bucket).
  // teamIds (self + descendants) is always computed for the approve-eligibility check,
  // so an "All"-mode user can still only approve visits whose approver is in their team.
  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const { data, error } = await supabase.from("Users").select("userId, parentId");
        if (error) throw error;
        const descendants = getAllDescendantIds(data, userId);
        setTeamIds(descendants);
        const me = data.find((u) => u.userId === userId);
        const isRoot = me?.parentId === null || me?.parentId === undefined;
        setSvVisibility(svShowMode === "All" || isRoot ? { scope: "all" } : { userIds: descendants });
      } catch (e) {
        console.error("CpSourcing sv hierarchy resolve error:", e);
        setTeamIds([userId]);
        setSvVisibility(svShowMode === "All" ? { scope: "all" } : { userIds: [userId] });
      }
    })();
  }, [userId, svShowMode]);

  // Load the complete set for this user's visibility.
  //
  // Called with tier="all" and no search, so the response is every assigned CP
  // — active ones included — each row carrying its own `tier` and its
  // ownerId/ownerName. That single payload is what lets the CP Source filter,
  // the tab counts and the search all work across the whole dataset instead of
  // just the rows a page happened to contain. The endpoint caps `limit` at 100,
  // so this arrives as ceil(total/100) sequential requests.
  // Generation counter for in-flight loads. A load is now a multi-request loop
  // rather than a single call, so it is easy for Refresh — or the reload that
  // follows saving a remark — to start a second one while the first is still
  // running. Without this, whichever loop finished last would win setAllRows,
  // letting a stale snapshot overwrite fresh post-save data, and the first to
  // finish would clear `loading` while the other was still going.
  const loadGeneration = useRef(0);

  const loadAll = useCallback(async () => {
    if (!userId || !visibility) return;
    const generation = ++loadGeneration.current;
    const isCurrent = () => loadGeneration.current === generation;
    setLoading(true);
    setError(null);
    try {
      const { rows } = await fetchAllPages(async (offset, limit) => {
        const res = await getDormantCps({
          userId, scope: visibility.scope, userIds: visibility.userIds,
          tier: "all", limit, offset,
        });
        if (!res?.success) throw new Error("Failed to load dormant CPs.");
        return { rows: res.data.rows, total: res.data.total };
      }, FETCH_PAGE_SIZE);
      if (isCurrent()) setAllRows(rows);
    } catch (e) {
      if (isCurrent()) {
        setError(e?.response?.data?.message || e.message || "Failed to load dormant CPs.");
      }
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [userId, visibility]);

  useEffect(() => { if (visibility) loadAll(); }, [visibility]); // eslint-disable-line react-hooks/exhaustive-deps

  // CP Source options — every owner across the whole dataset, so the dropdown
  // is complete from the first render rather than growing as pages load.
  const sourceOptions = useMemo(() => collectSourceOptions(allRows), [allRows]);

  const toggleSource = (ownerId) =>
    setPendingSources((prev) =>
      prev.includes(ownerId) ? prev.filter((id) => id !== ownerId) : [...prev, ownerId]
    );

  // One config, handed to the desktop panel and the mobile sheet alike, so the
  // two can't drift in options, wording or staging behaviour.
  const sourceFilterCategories = [
    {
      label: "CP Source",
      options: sourceOptions,
      selectedIds: pendingSources,
      onToggle: toggleSource,
      emptyMessage: "No CP Sources Found",
      searchable: true,
      searchPlaceholder: "Search CP Source",
    },
  ];

  const clearSources = () => { setPendingSources([]); setAppliedSources([]); };

  // Chips mirror the *applied* sources. Removing one applies immediately and
  // drops the id from pending too, so reopening the sheet agrees with the chips.
  const activeSourceChips = useMemo(
    () =>
      appliedSources.map((id) => ({
        id,
        label: sourceOptions.find((o) => o.id === id)?.label || String(id),
      })),
    [appliedSources, sourceOptions]
  );

  const removeSourceChip = (ownerId) => {
    setPendingSources((prev) => prev.filter((id) => id !== ownerId));
    setAppliedSources((prev) => prev.filter((id) => id !== ownerId));
  };

  // Narrowing pipeline, in the order the user reads the screen:
  //
  //   allRows ──CP Source + search──▶ narrowed ──tier tab──▶ tierRows
  //                                      │                      │
  //                                  tab counts            rendered slice
  //
  // Counts come off `narrowed` — after the CP Source filter and the search, but
  // before the tier tab — which is what makes every tab count describe the
  // current filter while still letting you switch tabs within it.
  const narrowed = useMemo(
    () => narrowRows(allRows, { sources: appliedSources, search }),
    [allRows, appliedSources, search]
  );

  const displayTiers = useMemo(() => countTiers(narrowed), [narrowed]);

  // Every row matching the current filter + tab. Drives the counts on the
  // scroll sentinel and how many rows remain to reveal.
  const tierRows = useMemo(() => selectTier(narrowed, activeTier), [narrowed, activeTier]);

  // What actually renders: a slice, grown REVEAL_STEP at a time on the same page.
  const visibleRows = useMemo(() => tierRows.slice(0, visibleCount), [tierRows, visibleCount]);

  // Any change of narrowing starts the reveal over, so applying a filter never
  // leaves you scrolled into a list you can no longer see the top of.
  useEffect(() => { setVisibleCount(REVEAL_STEP); }, [activeTier, search, appliedSources]);

  const hasMoreToReveal = visibleCount < tierRows.length;

  // Infinite scroll. The sentinel node is held in state rather than a ref so
  // this effect re-runs when it mounts and unmounts — a plain ref would still
  // be null on the first pass and nothing would ever be observed.
  const [revealSentinel, setRevealSentinel] = useState(null);

  useEffect(() => {
    if (!hasMoreToReveal) return;
    // browserslist (">0.2%, not dead") admits browsers without
    // IntersectionObserver, and CRA ships no polyfill for it. Rather than
    // stranding those users on the first 20 rows with no way to reach the
    // rest, render the whole list for them.
    if (typeof IntersectionObserver === "undefined") {
      setVisibleCount(tierRows.length);
      return;
    }
    if (!revealSentinel) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        setVisibleCount((c) => growReveal(c, REVEAL_STEP, tierRows.length));
      },
      { root: findScrollParent(revealSentinel), rootMargin: REVEAL_MARGIN }
    );
    io.observe(revealSentinel);
    return () => io.disconnect();
  }, [revealSentinel, hasMoreToReveal, tierRows.length]);

  // Tier tabs and search are pure client-side narrowing now — no refetch.
  const onTierClick = (tierKey) => {
    if (tierKey === activeTier) return;
    setActiveTier(tierKey);
  };

  const onSearchChange = (value) => setSearch(value);

  const handleCall = async (row) => {
    const agent_number = loggedInUser?.contactNumber;
    const caller_id = loggedInUser?.assignedCallerDid;
    if (!agent_number || !row.phone) {
      toast.error("Missing agent or customer number", { position: "top-center", autoClose: 3000 });
      return;
    }
    setCallingCpId(row.cpId);
    try {
      const result = await makeCall({ agent_number, destination_number: row.phone, caller_id });
      if (result?.success) toast.success("Call initiated successfully!", { position: "top-center", autoClose: 3000 });
      else toast.error(result?.message || "Failed to initiate call", { position: "top-center", autoClose: 3000 });
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to initiate call", { position: "top-center", autoClose: 3000 });
    } finally {
      setCallingCpId(null);
    }
  };

  return (
    <Box sx={{ background: C.page, minHeight: "100vh", fontFamily: C.sans }}>
      {/* STICKY TOP — header, pills, tiers + search stay pinned; only the list below
          scrolls. Full width (no max-width) to match the other main tabs. */}
      <Box sx={{ position: "sticky", top: 0, zIndex: 10, background: C.page, px: "2.4rem", pt: { xs: 2, sm: 3 } }}>

        {/* HEADER */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: "2rem" }}>
          <Box>
            <Typography
              sx={{
                fontFamily: "Roboto-Regular",
                fontStyle: "normal",
                fontWeight: 400,
                fontSize: "3.2rem",
                lineHeight: "4rem",
                color: "#202124",
              }}
            >
              CP Sourcing
            </Typography>
            <Typography sx={{ fontSize: "1.4rem", lineHeight: "2rem", color: "#667085", mt: "0.4rem" }}>
              Your dormant channel partners — call & log every discussion
            </Typography>
          </Box>
          <IconButton
            onClick={loadAll}
            title="Refresh"
            sx={{ border: `1px solid ${C.border}`, borderRadius: "0.8rem", background: C.white, "&:hover": { background: C.indigoSoft } }}
          >
            <RefreshIcon sx={{ fontSize: 18, color: C.txt3 }} />
          </IconButton>
        </Box>

        {/* VIEW PILLS (Dormant CPs / My Calls / CP Site Visit) + FILTER/SEARCH.
            The three pills use only about a third of the row, so the filter and
            search sit in the spare width on the right. They deliberately do NOT
            live on the tier row below: that row needs its full width, and
            shrinking it to fit the filter is what produced a horizontal
            scrollbar across the tier tabs. */}
        <Box sx={{
          display: "flex", alignItems: "flex-end", justifyContent: "space-between",
          gap: "2rem", mb: "2rem",
        }}>
          <Box sx={{
            display: "flex", flexWrap: "wrap", gap: "4px", p: "0.4rem", borderRadius: "0.8rem",
            background: C.page, border: `1px solid ${C.border}`,
            width: { xs: "100%", sm: "fit-content" }, flexShrink: 0,
          }}>
            {[{ key: "cps", label: "Dormant CPs" }, { key: "calls", label: "My Calls" }, { key: "svapproval", label: "CP Site Visit" }].map((v) => (
              <Box
                key={v.key}
                onClick={() => setView(v.key)}
                sx={{
                  px: "1.4rem", py: "0.8rem", borderRadius: "0.8rem", cursor: "pointer",
                  fontSize: 12.5, fontWeight: 500, fontFamily: C.sans,
                  transition: "all .2s ease", userSelect: "none",
                  ...(view === v.key
                    ? { background: C.indigo, color: "#fff", boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)" }
                    : { background: C.indigoSoft, color: C.indigo, border: `1px solid ${C.indigoBd}` }),
                }}
              >
                {v.label}
              </Box>
            ))}
          </Box>

          {/* Only the Dormant CPs view is filtered — My Calls and CP Site Visit
              carry their own controls — so this is gated on view === "cps".
              align="right" because LeadsFilterPanel anchors its dropdown to the
              trigger's left edge by default, which from the right of the row
              would push the panel off the viewport. */}
          {view === "cps" && (
            <Box sx={{
              display: isMobile ? "none" : "flex",
              alignItems: "flex-end", gap: "2rem", flexShrink: 0,
            }}>
              <LeadsFilterPanel
                categories={sourceFilterCategories}
                onClearPending={() => setPendingSources([])}
                onClear={clearSources}
                onApply={() => setAppliedSources(pendingSources)}
                align="right"
              />

              {/* 25rem is the Leads standard search width
                  (.wrapperSearchBar in Leads.module.css). */}
              <Box sx={{ width: "25rem", maxWidth: "100%" }}>
                <SearchBar
                  id="cpSourcingSearch"
                  placeholder="Search CP name or ID…"
                  search={(e) => onSearchChange(e.target.value)}
                  width="100%"
                />
              </Box>
            </Box>
          )}
        </Box>

        {view === "cps" && (<>

          {/* TIER SUBTABS — full width, on their own row. The filter and search
              live on the view-pills row above; putting them here forced the tabs
              to shrink, which turned overflowX into a visible horizontal
              scrollbar across the tabs. */}
          <Box sx={{
            display: "flex", gap: "2rem", mb: "2rem",
            borderBottom: `1px solid ${C.border}`, overflowX: "auto",
          }}>
            {CHIP_KEYS.map((key) => {
              const meta = TIER_META[key];
              const selected = activeTier === key;
              return (
                <Box
                  key={key}
                  onClick={() => onTierClick(key)}
                  sx={{
                    display: "flex", flexDirection: "column", gap: 0.4,
                    py: 1.1, cursor: "pointer", whiteSpace: "nowrap", userSelect: "none",
                    fontFamily: C.sans,
                    color: selected ? meta.color : C.txt3,
                    borderBottom: `2px solid ${selected ? meta.color : "transparent"}`,
                    mb: "-1px",
                    transition: "color .15s, border-color .15s",
                    "&:hover": { color: meta.color },
                  }}
                >
                  <Box sx={{
                    display: "inline-flex", alignItems: "center", gap: 0.7,
                    fontSize: 13.5, lineHeight: 1, fontWeight: selected ? 600 : 500,
                  }}>
                    {meta.label}
                    <Box component="span" sx={{
                      fontFamily: C.mono, fontSize: 12, fontWeight: 600,
                      color: selected ? meta.color : C.txt4,
                    }}>
                      {displayTiers[key] ?? 0}
                    </Box>
                  </Box>
                  <Box component="span" sx={{
                    fontSize: 10.5, fontWeight: 500, lineHeight: 1.2, color: C.txt4,
                  }}>
                    {meta.sub}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* MOBILE FILTER + SEARCH — stays on its own row below the tier tabs;
              there is no spare width to fold it into the pills row on a phone.
              Both search UIs stay mounted and are toggled with CSS rather than
              conditionally rendered. The shared SearchBar is uncontrolled, so
              unmounting it across the 820px breakpoint would drop its text while
              `search` state stayed set — leaving a blank box over a filtered list. */}
          <Box sx={{ display: isMobile ? "block" : "none", mb: "2rem" }}>
            <MobileFilterBar
              searchValue={search}
              onSearchChange={onSearchChange}
              searchPlaceholder="Search CP name or ID…"
              filterCount={appliedSources.length}
              onOpenFilters={() => setMobileFilterOpen(true)}
            />
            <ActiveFilterChips chips={activeSourceChips} onRemove={removeSourceChip} />
          </Box>
        </>)}
      </Box>

      {/* SCROLLABLE CONTENT — only this area scrolls under the pinned top */}
      <Box sx={{ px: "2.4rem", pb: { xs: 2, sm: 3 }, pt: 1 }}>
        {view === "calls" && <MyCallsView userId={userId} />}

        {view === "svapproval" && (
          <CpSiteVisitApprovalView
            userId={userId}
            visibility={svVisibility}
            teamIds={teamIds}
            allMode={cpSvAllMode}
            approveAccess={cpSvApprove}
            viewAccess={cpSvViewDetails}
          />
        )}

        {view === "cps" && (<>

          {/* STATES */}
          {error && (
            <Surface sx={{ textAlign: "center", py: 4, px: 2 }}>
              <Typography sx={{ color: C.red, mb: 1.5, fontSize: 13, fontFamily: C.sans }}>{error}</Typography>
              <Box sx={{ display: "inline-block", width: "auto" }}>
                <SharedButton
                  style={{
                    color: "#344054",
                    backgroundColor: "#ffffff",
                    fontSize: "1.4rem",
                    lineHeight: "2rem",
                    width: "auto",
                  }}
                  padding={"0.8rem 1.6rem"}
                  btnName={"Retry"}
                  clicked={loadAll}
                />
              </Box>
            </Surface>
          )}

          {!error && visibleRows.length === 0 && !loading && (
            <Surface sx={{ textAlign: "center", py: 6, px: 2 }}>
              {/* The filter now spans the whole dataset, so an empty result
                  really means "none match" — there is no "loaded so far"
                  caveat left to explain. */}
              {appliedSources.length > 0 && allRows.length > 0 ? (
                <Typography sx={{ color: C.txt3, fontSize: 13.5, fontFamily: C.sans }}>
                  No CPs from {appliedSources.length === 1 ? "this CP Source" : "these CP Sources"}
                  {activeTier && activeTier !== "all" ? " in this bucket" : ""}
                  {search.trim() !== "" ? " match your search" : ""}.
                </Typography>
              ) : (
                <Typography sx={{ color: C.txt3, fontSize: 13.5, fontFamily: C.sans }}>
                  No CPs {activeTier && activeTier !== "all" ? "in this bucket" : "assigned to you"} right now. 🎉
                </Typography>
              )}
            </Surface>
          )}

          {/* CP ROWS */}
          <Stack sx={{ gap: "2.4rem" }}>
            {visibleRows.map((row) => {
              const meta = TIER_META[row.tier] || TIER_META.neverActive;
              return (
                <CpCard key={row.cpId} sc={meta.color} elevation={0}>
                  <Box
                    onClick={() => setSelectedCp(row)}
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, p: "2.4rem", cursor: "pointer", flexWrap: "wrap" }}
                  >
                    <Avatar sx={{
                      width: 38, height: 38, fontSize: 15, fontWeight: 600,
                      bgcolor: meta.bg, color: meta.color, fontFamily: C.sans,
                    }}>
                      {(row.cpName || "?").charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* wraps on phones so the id + owner chip can't push the row wide */}
                      <Stack
                        direction="row" spacing={0.8} alignItems="baseline"
                        sx={{ minWidth: 0, flexWrap: "wrap", rowGap: 0.3 }}
                      >
                        <Typography sx={{ fontWeight: 600, fontSize: 13.5, color: C.txt1, fontFamily: C.sans }} noWrap>
                          {row.cpName || "—"}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: C.txt4, fontFamily: C.mono, flexShrink: 0 }}>
                          #{row.cpDisplayId}
                        </Typography>
                        {row.ownerId && row.ownerId !== userId && row.ownerName && (
                          <Box sx={{
                            fontSize: 10.5, fontWeight: 500, fontFamily: C.sans, flexShrink: 0,
                            color: C.indigo, background: C.indigoSoft, border: `1px solid ${C.indigoBd}`,
                            borderRadius: "6px", px: "6px", py: "1px", whiteSpace: "nowrap",
                          }}>
                            {row.ownerName}
                          </Box>
                        )}
                      </Stack>
                      <Typography sx={{ color: C.txt3, fontSize: 11.5, fontFamily: C.sans }} noWrap>
                        Last visit: {fmtDate(row.lastVisitMs)}
                        {row.daysDormant !== null && ` · ${row.daysDormant} days dormant`}
                        {` · ${row.sv90d} visits in 90d`}
                      </Typography>
                      {row.lastOutcome && (
                        <Typography sx={{ color: C.txt2, fontSize: 11.5, fontFamily: C.sans }} noWrap>
                          Last call: <b>{row.lastOutcome}</b>{row.lastRemark ? ` — ${row.lastRemark}` : ""}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{
                      display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap",
                      width: { xs: "100%", sm: "auto" },
                      justifyContent: { xs: "flex-start", sm: "flex-end" },
                    }}>
                    <Box sx={{ display: "inline-flex", whiteSpace: "nowrap", flexShrink: 0 }}>
                      <TierBadge tier={row.tier} />
                    </Box>
                    <SharedButton
                      style={{
                        color: "#344054",
                        backgroundColor: "#ffffff",
                        fontSize: "1.4rem",
                        lineHeight: "2rem",
                        width: "auto",
                      }}
                      padding={"0.8rem 1.6rem"}
                      btnName={"+ Add Activity"}
                      clicked={(e) => { e.stopPropagation(); setActivityCp(row); }}
                    />
                    <CallBtn
                      startIcon={<CallIcon sx={{ fontSize: 15 }} />}
                      disabled={!row.phone || callingCpId === row.cpId}
                      onClick={(e) => { e.stopPropagation(); handleCall(row); }}
                    >
                      {!row.phone ? "No number" : callingCpId === row.cpId ? "Calling…" : "Call"}
                    </CallBtn>
                    <IconButton
                      size="small"
                      title="View recent site visits"
                      onClick={(e) => { e.stopPropagation(); toggleSiteVisits(row.cpId); }}
                      sx={{ color: C.txt3 }}
                    >
                      <KeyboardArrowDownIcon
                        sx={{
                          fontSize: 20,
                          transition: "transform .2s",
                          transform: expandedCpId === row.cpId ? "rotate(180deg)" : "none",
                        }}
                      />
                    </IconButton>
                    </Box>
                  </Box>

                  {/* View more → last 5 site visits (SM · date · project code) */}
                  <Collapse in={expandedCpId === row.cpId} unmountOnExit>
                    <Box sx={{ borderTop: `1px solid ${C.border2}`, background: C.border2, px: { xs: 1.5, sm: 2 }, py: 1.25 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 600, color: C.txt3, fontFamily: C.sans, mb: 0.75, textTransform: "uppercase", letterSpacing: ".3px" }}>
                        Recent site visits
                      </Typography>
                      {svLoadingCp === row.cpId ? (
                        <Box sx={{ textAlign: "center", py: 1.5 }}><CircularProgress size={18} sx={{ color: C.indigo }} /></Box>
                      ) : (svByCp[row.cpId] || []).length === 0 ? (
                        <Typography sx={{ fontSize: 11.5, color: C.txt4, fontFamily: C.sans, py: 0.5 }}>
                          No site visits recorded for this CP.
                        </Typography>
                      ) : (
                        <Stack spacing={0.6}>
                          {(svByCp[row.cpId] || []).map((v) => (
                            <Box
                              key={v.siteVisitId}
                              sx={{
                                display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px 14px",
                                fontFamily: C.sans, fontSize: 12,
                              }}
                            >
                              <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: C.indigo, flexShrink: 0 }} />
                              <Typography sx={{ fontSize: 12, color: C.txt2, fontFamily: C.sans }}>
                                <span style={{ color: C.txt4, fontWeight: 500 }}>SM: </span>
                                <span style={{ color: C.txt1, fontWeight: 500 }}>{v.smName || "—"}</span>
                              </Typography>
                              <Typography sx={{ fontSize: 12, color: C.txt2, fontFamily: C.sans }}>
                                <span style={{ color: C.txt4, fontWeight: 500 }}>Project: </span>
                                <span style={{ color: C.txt1, fontWeight: 500, fontFamily: C.mono }}>{v.projectShortCode || "—"}</span>
                              </Typography>
                              <Typography sx={{ fontSize: 12, color: C.txt2, fontFamily: C.sans }}>
                                <span style={{ color: C.txt4, fontWeight: 500 }}>Date: </span>
                                <span style={{ color: C.txt1, fontWeight: 500, fontFamily: C.mono }}>{fmtDate(Number(v.created_at))}</span>
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Collapse>
                </CpCard>
              );
            })}
          </Stack>

          {loading && <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={26} sx={{ color: C.indigo }} /></Box>}

          {/* Scroll sentinel. Coming into view reveals the next REVEAL_STEP
              rows, which are already in memory — so this is a spinner, not a
              button, and it never leaves the page or hits the network. */}
          {!loading && hasMoreToReveal && (
            <Box ref={setRevealSentinel} sx={{ textAlign: "center", py: 3 }}>
              <CircularProgress size={22} sx={{ color: C.indigo }} />
            </Box>
          )}

        </>)}
      </Box>

      {/* Overlays — mounted from state, independent of the active view */}
      {selectedCp && (
        <CpDetailDrawer
          cp={selectedCp}
          onClose={() => setSelectedCp(null)}
          onCall={() => handleCall(selectedCp)}
          onSaved={loadAll}
        />
      )}

      <AddActivityModal
        open={!!activityCp}
        cp={activityCp}
        onClose={() => setActivityCp(null)}
        onSaved={loadAll}
        onCpSiteVisit={(cp) => { setActivityCp(null); setSiteVisitCp(cp); }}
      />

      <AddCpSiteVisit
        open={!!siteVisitCp}
        cp={siteVisitCp}
        handleClose={() => setSiteVisitCp(null)}
      />

      {isMobile && (
        <MobileFilterSheet
          open={mobileFilterOpen}
          categories={sourceFilterCategories}
          onApply={() => setAppliedSources(pendingSources)}
          onClearAll={clearSources}
          onClose={() => setMobileFilterOpen(false)}
        />
      )}
    </Box>
  );
}
